import amexMark from '@/assets/cards/amex.svg'
import applePayMark from '@/assets/cards/apple-pay-logo.svg'
import googlePayMark from '@/assets/cards/google-pay-mark.svg'
import mastercardMark from '@/assets/cards/mastercard.svg'
import visaMark from '@/assets/cards/visa.svg'
import { cn } from '@/lib/utils'
import { walletLabel } from './format'

/** Finix sends `AMERICAN_EXPRESS` / `APPLE_PAY`; be tolerant of case, spaces and dashes. */
function normalize(value?: string | null): string {
  return (value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')
}

const LABELS: Record<string, string> = {
  '': 'Card',
  UNKNOWN: 'Card',
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  MASTER_CARD: 'Mastercard',
  AMERICAN_EXPRESS: 'Amex',
  AMEX: 'Amex',
  DISCOVER: 'Discover',
  JCB: 'JCB',
  DINERS_CLUB: 'Diners Club',
  DINERS: 'Diners Club',
  CHINA_UNIONPAY: 'UnionPay',
  CHINA_UNION_PAY: 'UnionPay',
  UNIONPAY: 'UnionPay',
  UNION_PAY: 'UnionPay',
}

/**
 * The marks the attendee app uses, copied into `src/assets/cards`. Each one
 * draws its own light card background, so they read on dark themes too.
 */
const MARKS: Record<string, string> = {
  VISA: visaMark,
  MASTERCARD: mastercardMark,
  MASTER_CARD: mastercardMark,
  AMERICAN_EXPRESS: amexMark,
  AMEX: amexMark,
}

/**
 * Apple's and Google's acceptance marks, as the attendee app ships them. Both
 * are card-shaped and draw their own light plate, so the black "Apple Pay"
 * wordmark and the grey "G Pay" pill stay legible on a dark theme untouched.
 */
const WALLET_MARKS: Record<string, { src: string; label: string }> = {
  APPLE_PAY: { src: applePayMark, label: 'Apple Pay' },
  GOOGLE_PAY: { src: googlePayMark, label: 'Google Pay' },
}

/** "AMERICAN_EXPRESS" → "Amex". Empty or unknown → "Card". */
// eslint-disable-next-line react-refresh/only-export-components -- the string helpers belong with the mark they mirror
export function brandLabel(brand?: string | null): string {
  const key = normalize(brand)
  const known = LABELS[key]
  if (known) return known
  // A network we have no name for: "SOME_NETWORK" reads better as "Some Network".
  return key.split('_').filter(Boolean).map((word) => word[0] + word.slice(1).toLowerCase()).join(' ')
}

/** "Apple Pay · Visa •••• 9736", for places that build a plain string. */
// eslint-disable-next-line react-refresh/only-export-components -- the string helpers belong with the mark they mirror
export function cardText(brand?: string | null, last4?: string | null, wallet?: string | null): string {
  return `${walletLabel(wallet)}${brandLabel(brand)}${last4 ? ` •••• ${last4}` : ''}`
}

/**
 * The wallet's mark when the card is one (Apple Pay / Google Pay), then the
 * network's logo (Visa / Mastercard / Amex) or a neutral name chip, then
 * "•••• 7000". An unrecognised wallet draws nothing.
 */
export function CardBrand({ brand, last4, wallet, className }: {
  brand?: string | null
  last4?: string | null
  wallet?: string | null
  className?: string
}) {
  const label = brandLabel(brand)
  const mark = MARKS[normalize(brand)]
  const walletMark = WALLET_MARKS[normalize(wallet)]
  return (
    <span className={cn('whitespace-nowrap', className)}>
      {walletMark
        ? <img src={walletMark.src} alt={walletMark.label} title={walletMark.label} className="mr-1 inline-block h-[17px] w-auto align-[-4px]" />
        : null}
      {mark
        ? <img src={mark} alt={label} className="inline-block h-[17px] w-auto align-[-4px]" />
        : (
          <span className="inline-block rounded border border-neutral-300 bg-neutral-100 px-1 text-xs font-medium text-neutral-600 align-[-1px] dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            {label}
          </span>
        )}
      {last4 ? ` •••• ${last4}` : ''}
    </span>
  )
}
